import React, { useMemo } from "react";

// ตรรกะและงานตกแต่งวันเกิด ใช้ร่วมกันหลายหน้า (หน้าแรก / โปรไฟล์ของฉัน)
// แก้ที่ไฟล์นี้ที่เดียว ทุกหน้าที่ import ไปจะเปลี่ยนตามทั้งหมด

// ตั้ง true เพื่อดูธีมวันเกิดโดยไม่ต้องรอถึงวันจริง (ใช้ตอนทดสอบเท่านั้น อย่าลืมกลับเป็น false)
const FORCE_BIRTHDAY_PREVIEW = false;

// วันที่ปัจจุบันตามเวลาไทย รูปแบบ YYYY-MM-DD
export const getThailandDate = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(new Date());
};

// วันนี้ตรงกับวันเกิดหรือไม่ (เทียบเฉพาะเดือน-วัน)
// เกิด 29 ก.พ. ในปีที่ไม่มี 29 ก.พ. ให้ถือว่าวันเกิดคือ 28 ก.พ.
export const isBirthdayToday = (birthday, todayYmd) => {
  if (!birthday || !todayYmd) return false;

  const birthMd = String(birthday).slice(5, 10);
  const todayMd = String(todayYmd).slice(5, 10);
  if (birthMd === todayMd) return true;
  if (birthMd !== "02-29") return false;

  const yearHasLeapDay = new Date(`${String(todayYmd).slice(0, 4)}-02-29T00:00:00`).getDate() === 29;
  return !yearHasLeapDay && todayMd === "02-28";
};

// อายุที่ครบในวันเกิดปีนี้
export const getBirthdayAge = (birthday, todayYmd) => {
  const birthYear = Number(String(birthday || "").slice(0, 4));
  const todayYear = Number(String(todayYmd || "").slice(0, 4));
  if (!birthYear || !todayYear || todayYear <= birthYear) return null;
  return todayYear - birthYear;
};

const BIRTHDAY_WISHES = [
  "ขอให้ปีนี้เต็มไปด้วยเรื่องดี ๆ สุขภาพแข็งแรง และมีความสุขในทุกวัน",
  "ขอให้สิ่งที่ตั้งใจไว้สำเร็จทุกอย่าง และมีแต่รอยยิ้มตลอดทั้งปี",
  "ขอให้โชคดี มีความสุข และเจอแต่โอกาสดี ๆ ในปีนี้",
  "ขอให้มีแรงและมีความสุขกับทุกสิ่งที่ทำ ทั้งเรื่องงานและเรื่องส่วนตัว",
  "ขอบคุณที่เป็นส่วนหนึ่งของทีม ขอให้ปีนี้เป็นปีที่ดีที่สุดของคุณ",
];

// เลือกคำอวยพรจากวันเกิด เพื่อให้ข้อความคงที่ ไม่เปลี่ยนทุกครั้งที่ re-render
export const pickBirthdayWish = (birthday) => {
  const digits = String(birthday || "").replace(/\D/g, "");
  const seed = Number(digits.slice(-4)) || 0;
  return BIRTHDAY_WISHES[seed % BIRTHDAY_WISHES.length];
};

// ข้อมูลวันเกิดของผู้ใช้ที่ล็อกอินอยู่
// วันเกิดมาจาก record พนักงานที่ผูกกับ user (profile.employee)
// ถ้า user ไม่ได้ผูกกับพนักงาน จะไม่มีวันเกิดให้เทียบ การ์ดวันเกิดก็จะไม่ขึ้น
export const getBirthdayInfo = (profile, todayYmd = getThailandDate()) => {
  const birthday = profile?.employee?.birthday || null;
  const isBirthday = FORCE_BIRTHDAY_PREVIEW || isBirthdayToday(birthday, todayYmd);

  return {
    isBirthday,
    birthdayAge: isBirthday ? getBirthdayAge(birthday, todayYmd) : null,
    birthdayWish: isBirthday ? pickBirthdayWish(birthday) : "",
    birthdayName:
      profile?.employee?.nickname || profile?.employee?.first_name || profile?.full_name_th || profile?.full_name_en || "",
  };
};

export const BIRTHDAY_HERO_BACKGROUND =
  "radial-gradient(circle at top right, rgba(255, 233, 128, 0.55), transparent 30%), radial-gradient(circle at bottom left, rgba(255, 138, 200, 0.45), transparent 34%), linear-gradient(135deg, #ff6a88 0%, #ff9a44 46%, #7a5cff 100%)";

export const BIRTHDAY_HERO_SHADOW = "0 28px 60px rgba(180, 60, 110, 0.28)";

// วงกลมตกแต่งมุมล่างซ้ายของ hero เปลี่ยนจากฟ้าเป็นเหลืองทองตอนวันเกิด
export const BIRTHDAY_GLOW = "radial-gradient(circle, rgba(255, 226, 122, 0.32) 0%, rgba(255, 226, 122, 0) 72%)";

const CONFETTI_COLORS = ["#ffd76a", "#ff9ec4", "#8ee6ff", "#b9a4ff", "#ffffff", "#8fe3b0"];
const CONFETTI_SHAPES = ["", " ptn-confetti--round", " ptn-confetti--ribbon"];

// พลุกระดาษ 26 ชิ้น สุ่มตำแหน่ง/สี/จังหวะครั้งเดียว แล้ว cache ไว้
// การ์ดที่ใช้ component นี้ re-render บ่อย (เช่นหน้าแรกมีนาฬิกาเดินทุกวินาที)
// ถ้าสุ่มใหม่ทุกรอบ พลุจะกระตุกรีเซ็ตตลอด
const buildConfetti = () =>
  Array.from({ length: 26 }, (_, index) => ({
    id: index,
    left: Math.random() * 100,
    delay: -Math.random() * 9,
    duration: 7 + Math.random() * 6,
    drift: Math.round((Math.random() - 0.5) * 160),
    spin: 360 + Math.round(Math.random() * 720),
    color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
    shape: CONFETTI_SHAPES[index % CONFETTI_SHAPES.length],
  }));

// หมวกปาร์ตี้ ของขวัญ และไอเทมงานวันเกิด ลอยอยู่รอบการ์ด
// ตัวใหญ่ ใช้ clamp เพื่อไม่ให้ล้นการ์ดบนจอเล็ก และหรี่ opacity ลงกันบังข้อความ
const PARTY_ITEMS = [
  { emoji: "🎉", top: "35%", left: "1%", size: "clamp(6.4rem, 8vw, 14rem)", opacity: 0.9, duration: 3.2, delay: 0, tilt: "-8deg" },
  { emoji: "🎁", top: "70%", left: "3%", size: "clamp(6.8rem, 9vw, 16rem)", opacity: 0.92, duration: 3.8, delay: 0.4, tilt: "6deg" },
  { emoji: "🥳", top: "6%", right: "3%", size: "clamp(6.8rem, 9vw, 15rem)", opacity: 0.9, duration: 3.4, delay: 0.8, tilt: "7deg" },
  { emoji: "🎊", top: "62%", right: "6%", size: "clamp(6rem, 8vw, 13rem)", opacity: 0.85, duration: 4.2, delay: 0.2, tilt: "-5deg" },
  { emoji: "🎈", top: "32%", right: "0%", size: "clamp(5.2rem, 6vw, 10rem)", opacity: 0.8, duration: 3.6, delay: 1.1, tilt: "5deg" },
];

// พลุกระดาษ + ไอเทมปาร์ตี้ ครอบเต็มการ์ดที่เป็น position: relative
// สไตล์และ keyframes อยู่ใน assets/css/style.css (.ptn-birthday-decor, .ptn-confetti, .ptn-party-item)
export const BirthdayDecor = () => {
  const confetti = useMemo(buildConfetti, []);

  return (
    <div className="ptn-birthday-decor" aria-hidden="true">
      {confetti.map((piece) => (
        <span
          key={piece.id}
          className={`ptn-confetti${piece.shape}`}
          style={{
            left: `${piece.left}%`,
            background: piece.color,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
            "--ptn-drift": `${piece.drift}px`,
            "--ptn-spin": `${piece.spin}deg`,
          }}
        />
      ))}

      {PARTY_ITEMS.map((item) => (
        <span
          key={item.emoji}
          className="ptn-party-item"
          style={{
            top: item.top,
            left: item.left,
            right: item.right,
            fontSize: item.size,
            opacity: item.opacity,
            animationDuration: `${item.duration}s`,
            animationDelay: `${item.delay}s`,
            "--ptn-tilt": item.tilt,
          }}
        >
          {item.emoji}
        </span>
      ))}
    </div>
  );
};

// ป้าย "วันนี้เป็นวันเกิดของคุณ · ครบ N ปี" ใช้ได้ทั้ง 2 หน้า
export const BirthdayBadge = ({ birthdayAge, className = "" }) => (
  <div
    className={`d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold ${className}`}
    style={{
      width: "fit-content",
      background: "rgba(255,255,255,0.22)",
      border: "1px solid rgba(255,255,255,0.35)",
      color: "#fff",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
    }}
  >
    <span style={{ fontSize: "1.1rem" }}>🎂</span>
    <span>วันนี้เป็นวันเกิดของคุณ{birthdayAge ? ` · ครบ ${birthdayAge} ปี` : ""}</span>
  </div>
);
